/**
 * groovi-animations.js — GSAP motion layer (Groovi frontend stack)
 *
 * Owns ALL scroll-triggered / multi-step / entrance / counter motion, per the
 * frontend layering rules in CLAUDE.md ("Theme Architecture → Frontend layering"
 * + "Motion & Navigation → CSS vs GSAP"). Simple hover, ambient loops (marquee),
 * and reduced-motion fallbacks stay in CSS using the --groovi-motion-* tokens.
 *
 * Load contract (self-hosted, deferred, in snippets/scripts.liquid — shared by
 * theme.liquid + password.liquid). Order is load-bearing:
 *   gsap.min.js → scrolltrigger.min.js → alpine.min.js → groovi-animations.js
 * GSAP + ScrollTrigger are therefore plain globals by the time this runs.
 *
 * To add motion to a section: write a self-contained init function that no-ops
 * when its target is absent (guard with a querySelector check), then call it
 * from initGrooviMotion(). Never run a CSS animation AND GSAP on the same
 * element (CLAUDE.md hard rule).
 */
(function () {
  'use strict';

  if (typeof window.gsap === 'undefined') {
    console.warn('[groovi-animations] GSAP not found — motion layer disabled.');
    return;
  }

  // Register ScrollTrigger + the free GSAP 3.13 plugins IF they loaded (DrawSVG
  // line-art, SplitText headline reveals — self-hosted in assets/, wired in
  // snippets/scripts.liquid). Pass only what's actually present so the layer
  // still works when a plugin is absent.
  gsap.registerPlugin.apply(gsap, [ScrollTrigger, window.DrawSVGPlugin, window.SplitText].filter(Boolean));

  // GROOVI (2026-07-01): the Horizon 4.x desktop "squeeze" is RETIRED (see
  // assets/scroll-container.js SQUEEZE_ACTIVE = false) — the root document scrolls
  // natively at every width, which is also ScrollTrigger's default scroller, so no
  // explicit scroller is needed. The 990px listener is kept only to re-measure
  // trigger positions when the layout reflows across the breakpoint.
  var grooviSqueezeQuery = window.matchMedia('(min-width: 990px)');
  ScrollTrigger.defaults({ scroller: window });
  grooviSqueezeQuery.addEventListener('change', function () {
    ScrollTrigger.refresh();
  });

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Public flags so per-section recipes can gate heavy motion (e.g. skip
  // parallax on mobile) without re-reading the media queries each time.
  window.GrooviMotion = {
    enabled: !prefersReducedMotion,
    prefersReducedMotion: prefersReducedMotion,
    isMobile: window.innerWidth < 768
  };

  /**
   * Product-card entrance — staggered fade + rise as each product grid scrolls
   * into view. First real recipe; proves the pipeline end-to-end (ScrollTrigger
   * + the window.GrooviMotion reduced-motion gate). Real Horizon selectors:
   * `.product-grid` (one container per collection / featured-collection section)
   * → `.product-card` (cards within). One ScrollTrigger per grid so every grid
   * on a page staggers its own cards independently.
   *
   * Degradation: if GSAP fails to load, this never runs and cards render at
   * their natural (visible) state — no inline opacity:0 is left behind.
   */
  function initProductCardEntrance() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    gsap.utils.toArray('.product-grid').forEach(function (grid) {
      var cards = grid.querySelectorAll('.product-card');
      if (!cards.length) return;
      // Skip the entrance for any grid already intersecting the initial viewport.
      // The server HTML paints cards at opacity 1; because GSAP is deferred, gsap.from's
      // immediateRender snaps those already-visible cards to opacity 0 ~250ms after paint
      // and fades them back — a visible "blink then load" on above-the-fold grids (the
      // reported collections-page flash; runtime-confirmed 2026-07-06). Content in view on
      // load must simply stay visible; the entrance is only for grids scrolled into view.
      var rect = grid.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) return;
      // once:true disables the ScrollTrigger right after it fires, so the later
      // ScrollTrigger.refresh() calls (document.fonts.ready ~L272, window.load
      // ~L295) can't land mid-entrance and freeze the staggered cards at partial
      // y-offsets — the "staircase" bug. clearProps strips the inline transform
      // on completion so no residual matrix(...) is ever left on the cards.
      gsap.from(cards, {
        scrollTrigger: { trigger: grid, start: 'top 80%', once: true },
        y: 24,
        opacity: 0,
        duration: 0.6,
        stagger: 0.08,
        ease: 'power2.out',
        clearProps: 'transform'
      });
    });
  }

  /**
   * Product-card hover lift — GSAP, per the frontend-stack rule that ALL motion
   * lives in the motion layer (not CSS). Lifts the card FACE (.product-card__content,
   * which carries the brand border + signature shadow) on hover; the GSAP entrance
   * above animates the PARENT (.product-card), so the two never animate the same
   * element (CLAUDE.md hard rule). Idempotent: a data-flag prevents double-binding
   * when re-scanned after a Theme Editor section reload. The lift moves outside the
   * card box — groovi-custom.css keeps .product-card overflow:visible so it isn't
   * cropped (owner-flagged concern).
   */
  function initProductCardHover() {
    if (!window.GrooviMotion.enabled) return;
    gsap.utils.toArray('.product-card').forEach(function (card) {
      if (card.dataset.grvHoverBound) return;
      var face = card.querySelector('.product-card__content');
      if (!face) return;
      card.dataset.grvHoverBound = 'true';
      // Crisp translate-only lift. The earlier tilt-lift (rotation:-0.6 + scale:1.01)
      // promoted the FACE to a GPU layer whose cached bitmap was then resampled by the
      // sub-degree rotation + fractional scale → a persistent slight blur across the
      // card text/ink-border/price the whole time it was hovered (V2 bug 1.2 — the
      // "card blurs when quick-add appears" report; native card_hover_effect is `none`,
      // so this tween was the sole source). translateY does NOT resample the layer, so
      // a pure y-lift keeps the card pin-sharp while preserving the sticker-peel cue.
      card.addEventListener('mouseenter', function () {
        gsap.to(face, { y: -6, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(face, { y: 0, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
      });
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Motion system ported from Pingo (PINGO-FEATURE-HANDOFF §4), renamed groovi-*.
  // ONLY the net-new engines that use NEW selectors are ported — the CSS class
  // palette (.groovi-fade-up / -wipe / -blur-in / -scale-soft / -slide-in /
  // -typewriter) stays CSS + .is-visible (groovi-wipe.js IntersectionObserver);
  // GSAP must NOT also drive those (CLAUDE.md: two systems on one element = bug).
  // ───────────────────────────────────────────────────────────────────────────

  // Scroll-enter trigger: fire as the element's top reaches ~90% down the
  // viewport. once:true == single-fire (no replay on scroll-back).
  var START = 'top 90%';
  // Marks an element whose tween was created, so the initial pass + section:load
  // rescans never double-bind the same node.
  var DONE = 'data-groovi-anim-done';
  function bind(el) {
    if (el.hasAttribute(DONE)) return false;
    el.setAttribute(DONE, '');
    return true;
  }

  // Movimento — the settings-driven preset engine. One initializer reads every
  // [data-groovi-anim] (emitted by snippets/groovi-motion-attrs.liquid from the
  // shared "Movimento" block settings) and applies the GSAP preset with the
  // merchant's knobs. Entrances (mode in) fire once then clearProps; the three
  // ambient presets (mode loop) run viewport-gated + mobile-lightened. Transform/
  // opacity only, except blur-in (filter, mobile-capped). reduced-motion → no tween.
  function initMotionPresets(root) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('[data-groovi-anim]').forEach(function (el) {
      var p = el.getAttribute('data-groovi-anim');
      if (!p || p === 'none') return;
      // Worm boots from its conditional module script, not dynamic import in
      // this classic script (Shopify's CDN rewrites that to browserless require).
      if (p === 'worm') return;
      // 'stepped' (spec 2.22B): CSS drives the stepped stop-motion loop; GSAP must
      // not also tween it. JS only publishes the merchant knobs (duration / step
      // count / amplitude / direction) as CSS custom props via setProperty — which
      // MERGES into the element's inline style, so it works even on hosts that
      // already carry a `style` attribute (image/text/svg), where a second `style`
      // attribute from Liquid would be dropped. Runs under reduced-motion too (the
      // vars are inert; the CSS media query disables the animation).
      if (p === 'stepped') {
        if (!bind(el)) return;
        var sDur = parseFloat(el.getAttribute('data-groovi-anim-dur')) || 1.4;
        var sCount = parseInt(el.getAttribute('data-groovi-anim-steps'), 10) || 12;
        var sAmt = parseFloat(el.getAttribute('data-groovi-anim-amt'));
        if (isNaN(sAmt)) sAmt = 24;
        var sAxis = el.getAttribute('data-groovi-anim-axis') || 'up';
        var sx = 0, sy = -sAmt;
        if (sAxis === 'down') { sy = sAmt; }
        else if (sAxis === 'left') { sy = 0; sx = -sAmt; }
        else if (sAxis === 'right') { sy = 0; sx = sAmt; }
        el.style.setProperty('--groovi-step-dur', sDur + 's');
        el.style.setProperty('--groovi-step-count', String(sCount));
        el.style.setProperty('--groovi-step-x', sx + 'px');
        el.style.setProperty('--groovi-step-y', sy + 'px');
        return;
      }
      if (!bind(el)) return;

      if (prefersReducedMotion) { gsap.set(el, { opacity: 1 }); return; }

      var mode   = el.getAttribute('data-groovi-anim-mode') || 'in';
      var dur    = parseFloat(el.getAttribute('data-groovi-anim-dur')) || 0.6;
      var delay  = parseFloat(el.getAttribute('data-groovi-anim-delay')) || 0;
      var bounce = el.getAttribute('data-groovi-anim-ease') !== 'smooth';
      var ease   = bounce ? 'back.out(1.6)' : 'power3.out';
      var amt    = parseFloat(el.getAttribute('data-groovi-anim-amt'));
      if (isNaN(amt)) amt = 24;

      if (mode === 'loop') {
        // Ambient loops honor anim_dur + anim_amt + anim_axis (spec 2.22B). durFactor
        // is 1.0 at the schema default (0.6s) so the tuned default durations are
        // unchanged; the amplitude divisors map anim_amt's default (24) back to each
        // preset's original amplitude → defaults render identically, only merchant
        // edits change them. Additive.
        var f = isMobile ? 0.5 : 1, tween;
        var durFactor = dur / 0.6;
        var laxis = el.getAttribute('data-groovi-anim-axis') || 'up';
        if (p === 'float') {
          var fa = (amt / 3) * f;                 // default 24 → 8 (unchanged)
          var fv = { duration: 2.4 * durFactor, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true };
          if (laxis === 'left')       fv.x = -fa;
          else if (laxis === 'right') fv.x = fa;
          else if (laxis === 'down')  fv.y = fa;
          else                        fv.y = -fa; // up
          tween = gsap.to(el, fv);
        } else if (p === 'drift') {
          tween = gsap.to(el, { x: (amt / 4) * f, y: -(amt / 6) * f, rotation: 1.5 * f, duration: 3.6 * durFactor, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true });
        } else {
          tween = gsap.to(el, { rotation: (amt / 9.6) * f, duration: 2.8 * durFactor, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true }); // sway
        }
        ScrollTrigger.create({
          trigger: el, start: 'top bottom', end: 'bottom top',
          onToggle: function (self) { self.isActive ? tween.resume() : tween.pause(); }
        });
        return;
      }

      // entrance presets
      var from = { opacity: 0 }, extra = {};
      if (p === 'fade-up')         from.y = amt;
      else if (p === 'slide-in')   from.x = -amt;
      else if (p === 'scale-soft') from.scale = 0.92;
      else if (p === 'pop')      { from.scale = 0.6; ease = bounce ? 'back.out(2.4)' : 'power3.out'; }
      else if (p === 'carimbo')  { from.scale = 0.9; from.rotation = -6; from.y = 14; ease = bounce ? 'back.out(1.7)' : 'power3.out'; }
      else if (p === 'blur-in')  { from.filter = 'blur(' + (isMobile ? 6 : 12) + 'px)'; extra.filter = 'blur(0px)'; }
      else if (p === 'wipe')     { from.opacity = 1; from.clipPath = 'inset(0 100% 0 0)'; extra.clipPath = 'inset(0 0% 0 0)'; }

      el.style.willChange = (p === 'blur-in') ? 'filter, opacity' : 'transform, opacity';
      var to = Object.assign({
        opacity: 1, x: 0, y: 0, scale: 1, rotation: 0,
        duration: dur, delay: delay, ease: ease,
        clearProps: 'transform,filter,clipPath,willChange'
      }, extra);
      if (el.getAttribute('data-groovi-anim-trigger') !== 'load') {
        to.scrollTrigger = { trigger: el, start: START, once: true };
      }
      gsap.fromTo(el, from, to);
    });
  }

  // Pattern drift (groovi-pattern block) — ambient GPU transform on the oversized
  // .groovi-pattern__layer (clipped by the shell's overflow, so the drift never
  // reveals an edge). Mobile-inclusive but lightened. Viewport-gated. RM → none.
  function initPatternDrift(root) {
    if (prefersReducedMotion) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('[data-groovi-pattern-motion="drift"]').forEach(function (host) {
      var layer = host.querySelector('.groovi-pattern__layer');
      if (!layer) return;
      if (!bind(host)) return;
      var amp = isMobile ? 1.5 : 3;
      var dur = isMobile ? 14 : 9;
      var tween = gsap.to(layer, {
        xPercent: amp, yPercent: -amp, duration: dur,
        ease: 'sine.inOut', repeat: -1, yoyo: true, paused: true
      });
      ScrollTrigger.create({
        trigger: host, start: 'top bottom', end: 'bottom top',
        onToggle: function (self) { self.isActive ? tween.play() : tween.pause(); }
      });
    });
  }

  // SplitText headline reveal. Opt-in via .groovi-headline (Custom CSS class hook).
  // Split AFTER document.fonts.ready (EasyPixel is self-hosted — splitting against
  // fallback metrics would mis-wrap, then reflow on swap). SplitText absent → reveal
  // without splitting. Hidden initial state + RM reveal live in groovi-custom.css.
  function animateHeadlines(root) {
    if (prefersReducedMotion) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('.groovi-headline').forEach(function (el) {
      if (!bind(el)) return;
      if (!window.SplitText) { gsap.set(el, { autoAlpha: 1 }); return; }
      var target = el.querySelector('h1, h2, h3, h4, h5, h6, p') || el;
      var split = new SplitText(target, { type: 'words', wordsClass: 'groovi-headline__word' });
      gsap.set(el, { autoAlpha: 1 });
      gsap.from(split.words, {
        yPercent: isMobile ? 60 : 100,
        opacity: 0,
        duration: isMobile ? 0.5 : 0.7,
        ease: 'power3.out',
        stagger: isMobile ? 0.03 : 0.05,
        scrollTrigger: { trigger: el, start: START, once: true }
      });
    });
  }

  // DrawSVG line-art draw-on. Opt-in via .groovi-draw wrapping inline STROKED SVG
  // (the natural carrier is the groovi-svg block: class_name "groovi-draw" + a
  // line-art SVG). Each shape self-draws on scroll-enter, once, staggered. Plugin
  // absent / RM → art left fully drawn (no-op).
  function initDrawSVG(root) {
    if (prefersReducedMotion || !window.DrawSVGPlugin) return;
    root.querySelectorAll('.groovi-draw').forEach(function (host) {
      if (!bind(host)) return;
      var strokes = host.querySelectorAll('path, line, polyline, polygon, circle, rect, ellipse');
      if (!strokes.length) return;
      gsap.set(strokes, { drawSVG: 0 });
      gsap.to(strokes, {
        drawSVG: '100%', duration: 1.1, ease: 'power2.out', stagger: 0.15,
        scrollTrigger: { trigger: host, start: START, once: true }
      });
    });
  }

  // Curtain reveal — a section-entrance clip un-clipping from the bottom up, once.
  // Opt-in via .groovi-curtain (Custom CSS class hook). Initial clipped state + RM
  // reveal live in groovi-custom.css. Mobile-inclusive (cheap GPU clip+transform).
  function animateCurtains(root) {
    if (prefersReducedMotion) return;
    root.querySelectorAll('.groovi-curtain').forEach(function (el) {
      if (!bind(el)) return;
      gsap.fromTo(el,
        { clipPath: 'inset(100% 0 0 0)', y: 24 },
        {
          clipPath: 'inset(0% 0 0 0)', y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: START, once: true }
        }
      );
    });
  }

  // Headlines wait on fonts; everything else runs immediately.
  function whenFontsReady(cb) {
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(cb); }
    else { cb(); }
  }

  // Global section scroll-reveal (spec 2.5). Opt-in site-wide via the "Globais — by
  // studiolab" setting → `.groovi-reveal-sections` on <body>. Every content section
  // after the first (the first is the LCP/above-fold candidate — never hidden) fades +
  // rises as it enters the viewport, once. The initial hidden state is a
  // reduced-motion-gated CSS opacity pre-hide (groovi-custom.css) — opacity + transform
  // only, so there is NO layout shift (CLS). This function must run even when motion is
  // disabled (mobile/RM), because the CSS may still have pre-hidden the sections — in
  // that case it reveals them instantly. Sections that carry explicit Movimento attrs
  // are left to initMotionPresets. Only clears `transform` on complete so the inline
  // opacity:1 keeps beating the CSS pre-hide.
  function initSectionReveal(root) {
    if (!document.body.classList.contains('groovi-reveal-sections')) return;
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('#MainContent > .shopify-section ~ .shopify-section');
    els.forEach(function (el) {
      if (el.hasAttribute('data-groovi-anim')) return;
      if (!bind(el)) return;
      var animate = window.GrooviMotion.enabled && !prefersReducedMotion && window.ScrollTrigger;
      if (!animate) { gsap.set(el, { opacity: 1, y: 0 }); return; }
      el.style.willChange = 'transform, opacity';
      gsap.fromTo(el,
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
          clearProps: 'transform,willChange',
          scrollTrigger: { trigger: el, start: 'top 90%', once: true }
        });
    });
  }

  // Page transition (spec 2.6). Entrance-only, GSAP, opt-in via "Transição de página"
  // (Globais) → `.groovi-page-transition` on <body>. On load the page content
  // (#MainContent) fades + rises in, softening the abrupt native full-page swap.
  // DELIBERATELY no link/click interception: native navigation proceeds normally, so
  // analytics/pixel fire exactly as before and navigation can never be delayed or
  // broken by JS — the highest risks the spec flagged. Runs once per page load. The
  // initial hidden state is a reduced-motion-gated opacity pre-hide (groovi-custom.css);
  // transform+opacity only → no CLS. Off by default (it trades a little LCP for the
  // reveal — enable + measure before shipping). Reveals instantly under RM/disabled.
  function initPageTransition() {
    if (!document.body.classList.contains('groovi-page-transition')) return;
    var main = document.getElementById('MainContent');
    if (!main || !bind(main)) return;
    if (!window.GrooviMotion.enabled || prefersReducedMotion) { gsap.set(main, { opacity: 1, y: 0 }); return; }
    main.style.willChange = 'transform, opacity';
    gsap.fromTo(main,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'transform,willChange' });
  }

  // Scan freshly-added/ present elements for the ported engines. Safe to re-run
  // (the DONE attr dedupes). Excludes the existing product-card recipes, which
  // have their own guards + lifecycle below.
  function scanPortedMotion(root) {
    initMotionPresets(root);
    initPatternDrift(root);
    initDrawSVG(root);
    animateCurtains(root);
    initSectionReveal(root);
    whenFontsReady(function () { animateHeadlines(root); ScrollTrigger.refresh(); });
  }

  // Number count-up (theme task 6, Counter block). Animates [data-groovi-counter]
  // from start → end on scroll-into-view (once). Reduced-motion / disabled →
  // renders the final value immediately. pt-BR number formatting + prefix/suffix.
  function initCounters(root) {
    var els = (root || document).querySelectorAll('[data-groovi-counter]');
    for (var i = 0; i < els.length; i++) {
      (function (el) {
        if (el.dataset.grooviCounterBound) return;
        el.dataset.grooviCounterBound = '1';
        var end = parseFloat(el.dataset.grooviCounterEnd) || 0;
        var start = parseFloat(el.dataset.grooviCounterStart) || 0;
        var dur = parseFloat(el.dataset.grooviCounterDuration) || 2;
        var decimals = parseInt(el.dataset.grooviCounterDecimals || '0', 10) || 0;
        var prefix = el.dataset.grooviCounterPrefix || '';
        var suffix = el.dataset.grooviCounterSuffix || '';
        function fmt(v) {
          return prefix + Number(v).toLocaleString('pt-BR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
          }) + suffix;
        }
        var animate = window.GrooviMotion.enabled && !prefersReducedMotion && typeof gsap !== 'undefined';
        if (!animate) { el.textContent = fmt(end); return; }
        el.textContent = fmt(start);
        var io = new IntersectionObserver(function (entries) {
          for (var j = 0; j < entries.length; j++) {
            if (!entries[j].isIntersecting) continue;
            io.unobserve(el);
            var obj = { v: start };
            gsap.to(obj, {
              v: end, duration: dur, ease: 'power1.out',
              onUpdate: function () { el.textContent = fmt(obj.v); }
            });
          }
        }, { threshold: 0.4 });
        io.observe(el);
      })(els[i]);
    }
  }

  function initGrooviMotion() {
    initCounters(document); // handles reduced-motion / disabled internally
    if (!window.GrooviMotion.enabled) {
      // Even with reduced motion, the preset engine must reveal hidden initial
      // states (it sets opacity:1 internally); the palette CSS handles its own RM.
      initMotionPresets(document);
      initSectionReveal(document); // reveal any CSS-pre-hidden sections instantly
      initPageTransition();        // reveal #MainContent instantly if it was pre-hidden
      return;
    }
    initProductCardEntrance();
    initProductCardHover();
    initPageTransition(); // once per page load (not on section:load)
    scanPortedMotion(document);
  }

  // Defer the motion setup until AFTER the browser's first paint (perf, 2026-07-01).
  // initGrooviMotion() creates every ScrollTrigger and runs an initial refresh —
  // `_refreshAll` measures the whole document, forcing a synchronous reflow. Running
  // it at DOMContentLoaded blocks the main thread inside the critical rendering window,
  // so the LCP hero can't paint until the thrash finishes (measured ~1.5s of "render
  // delay" on throttled mobile). A double-rAF fires right after the next paint, so the
  // hero paints first and GSAP inits ~one frame later — imperceptible for entrance
  // reveals (they're post-load by nature), but it takes the reflow off the LCP path.
  function scheduleGrooviMotionInit() {
    requestAnimationFrame(function () {
      requestAnimationFrame(initGrooviMotion);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleGrooviMotionInit);
  } else {
    scheduleGrooviMotionInit();
  }

  // Recalculate trigger positions after late-loading content (images, fonts)
  // and after Theme Editor section re-renders in design mode.
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:load', function () {
    ScrollTrigger.refresh();
    initProductCardHover(); // bind hover on cards added by the editor (data-flag guards rebind)
    initCounters(document); // bind counters added by the editor (data-flag guards rebind)
    if (window.GrooviMotion.enabled) scanPortedMotion(document);
    else initMotionPresets(document);
  });
  document.addEventListener('shopify:section:unload', function () { ScrollTrigger.refresh(); });
})();
